import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import FallbackSpinner from '@/components/FallbackSpinner';
import { canAccessAnyBackofficeSection } from '@/config/backoffice-permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { recordPermissionBasedRouteDenied } from '@/lib/rbac-monitor';
import type { NavigationPermissionRequirement } from '@/types/navigation-permissions';

interface PermissionBasedRouteProps {
  requiredPermission: NavigationPermissionRequirement;
}

/**
 * Route guard driven by backend permissions. Navigation-only — API enforces auth.
 */
export const PermissionBasedRoute: React.FC<PermissionBasedRouteProps> = ({
  requiredPermission,
}) => {
  const { hasPermission, isLoading } = usePermissions();
  const { pathname } = useLocation();

  if (isLoading) {
    return <FallbackSpinner />;
  }

  if (!hasPermission(requiredPermission.feature, requiredPermission.action)) {
    recordPermissionBasedRouteDenied(
      requiredPermission.feature,
      requiredPermission.action,
      pathname
    );
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};

/** Back Office contains several sections. Entry is allowed when any section is. */
export const BackofficeRoute: React.FC = () => {
  const { hasPermission, isLoading } = usePermissions();
  const { pathname } = useLocation();

  if (isLoading) {
    return <FallbackSpinner />;
  }

  if (!canAccessAnyBackofficeSection(hasPermission)) {
    recordPermissionBasedRouteDenied('references_catalog', 'list', pathname);
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};

export default PermissionBasedRoute;
